import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Printer, ScrollText, Radar } from "lucide-react";
import { FluxMark } from "../components/flux/Decor";
import { SKINS } from "../components/matrix/skins";
import { API } from "../context/AuthContext";

const display = "font-display";

const STATUS_DOT = {
  alert: "bg-rose-500 ring-rose-500/50",
  pending: "bg-purple-500 ring-purple-500/50",
  completed: "bg-blue-500 ring-blue-500/50",
};

const PAGE_BG = {
  fluxlab: "bg-[#0f1729]",
  courtroom: "bg-[#efe9da]",
  storybook: "bg-[#221723]",
  neon: "bg-[#04060d]",
};

export default function Report() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${shareId}`, { withCredentials: false })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Report not found"));
  }, [shareId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1729] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="report-error">
        <FluxMark className="w-12 h-12 opacity-50" />
        <p className="text-gray-300 text-sm">{error}</p>
        <Link to="/" className="text-[#f59e0b] text-xs hover:underline">← Flux Capacitor home</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0f1729] flex items-center justify-center">
        <p className="text-[#f59e0b] font-mono text-xs animate-blink">Assembling the report...</p>
      </div>
    );
  }

  const skin = SKINS[data.skin] || SKINS.fluxlab;
  const pageBg = PAGE_BG[data.skin] || PAGE_BG.fluxlab;
  const isLight = data.skin === "courtroom";

  return (
    <div className={`min-h-screen ${pageBg} py-10 px-4 sm:px-6`} data-testid="report-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className={`border rounded-2xl p-8 ${skin.panel}`}>
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <div className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] mb-3 ${isLight ? "text-[#7c5e10]" : "text-[#f59e0b]"}`}>
                <FluxMark className="w-5 h-5" /> Timeline Report
              </div>
              <h1 className={`text-2xl md:text-3xl font-bold ${skin.title}`} data-testid="report-case-name">{data.case_name}</h1>
              <p className={`text-xs mt-2 ${skin.desc}`}>
                {data.owner_name && <>Prepared by {data.owner_name} · </>}
                {data.events.length} chronological events · Generated {new Date(data.created_at).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={() => window.print()}
              data-testid="report-print-btn"
              className={`print:hidden flex items-center gap-2 text-[11px] font-mono font-bold px-3 py-2 rounded-lg border transition-colors duration-200 ${isLight ? "border-[#7c5e10]/40 text-[#7c5e10] hover:bg-[#7c5e10]/10" : "border-white/15 text-gray-300 hover:bg-white/5"}`}
            >
              <Printer className="w-3.5 h-3.5" /> PRINT / PDF
            </button>
          </div>
        </div>

        <div className={`border rounded-2xl p-8 ${skin.panel}`}>
          <div className={`border-l-2 pl-6 ml-1 space-y-4 ${skin.rail}`} data-testid="report-timeline">
            {data.events.length === 0 ? (
              <p className={`text-xs italic ${skin.desc}`}>This timeline has no events yet.</p>
            ) : (
              data.events.map((ev) => (
                <div key={ev.id} className={`relative border p-4 rounded-xl space-y-2 ${skin.card}`}>
                  <div className={`absolute -left-[31px] top-5 w-2 h-2 rounded-full border-4 ${isLight ? "border-[#efe9da]" : "border-[#0f1729]"} ring-2 ${STATUS_DOT[ev.status] || STATUS_DOT.completed}`} />
                  <div className="flex flex-wrap justify-between items-center gap-2 text-[10px] font-mono">
                    <span className={`border px-2 py-0.5 rounded ${skin.chip}`}>{ev.label}</span>
                    <div className="flex gap-1">
                      {ev.tracks.map((t) => (
                        <span key={t} className={`text-[9px] px-1 rounded uppercase font-bold ${isLight ? "bg-[#7c5e10]/10 text-[#7c5e10]" : "bg-black/20 text-gray-400"}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <h4 className={`font-bold text-sm ${skin.title}`}>{ev.title}</h4>
                  <p className={`text-xs leading-relaxed ${skin.desc}`}>{ev.description}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {data.insight && data.insight.narrative && (
          <div className={`border rounded-2xl p-8 space-y-6 ${skin.panel}`} data-testid="report-insights">
            <div>
              <div className={`flex items-center gap-2 mb-3 text-[10px] font-mono uppercase tracking-[0.2em] ${isLight ? "text-[#7c5e10]" : "text-[#f59e0b]"}`}>
                <ScrollText className="w-4 h-4" /> Narrative Summary
              </div>
              <div className="space-y-3">
                {data.insight.narrative.split(/\n\n+/).map((p, i) => (
                  <p key={i} className={`text-[13px] leading-relaxed ${skin.desc}`}>{p}</p>
                ))}
              </div>
            </div>
            {data.insight.patterns?.length > 0 && (
              <div>
                <div className={`flex items-center gap-2 mb-3 text-[10px] font-mono uppercase tracking-[0.2em] ${isLight ? "text-[#7c5e10]" : "text-[#2dd4bf]"}`}>
                  <Radar className="w-4 h-4" /> Pattern Insights
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.insight.patterns.map((p, i) => (
                    <div key={p.title || i} className={`border rounded-xl p-4 ${skin.card}`}>
                      <h5 className={`text-xs font-bold mb-1 ${skin.title}`}>{p.title}</h5>
                      <p className={`text-[11px] leading-relaxed ${skin.desc}`}>{p.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center py-4 print:hidden">
          <Link to="/" className={`inline-flex items-center gap-2 text-xs font-mono ${isLight ? "text-[#7c5e10]" : "text-gray-400"} hover:opacity-80 transition-opacity duration-200`}>
            <FluxMark className="w-4 h-4" /> Generated with Flux Capacitor — organize your own history
          </Link>
        </div>
      </div>
    </div>
  );
}
